import React from 'react';
import { Link } from 'react-router-dom';
import Tile from '../../../Components/Containers/Tile';
import Icon from '../../../Components/Icons/Icon';
import { useSession } from '../../../Components/Session';
import { useBankTransactionsToHandle } from '../../../Data/Payments';
import { Roles } from '../../../Data/Users';
import { encodeCriteria, toQueryString } from '../../../Services/helpers';

const BankTransactionsToHandle = () => {
    const { hasFeature, isRoleSufficient, powerSearch } = useSession();
    const canShowIncoming = isRoleSufficient(Roles.Manager) && hasFeature('BANK_RECONCILIATION');
    const canShowOutgoing = isRoleSufficient(Roles.Manager) && hasFeature('EXPENSES');
    const show = canShowIncoming || canShowOutgoing;
    const { counts } = useBankTransactionsToHandle(!show);

    if (!show || !counts) {
        return null;
    }

    const incoming = canShowIncoming ? (counts.incoming ?? 0) : 0;
    const outgoing = canShowOutgoing ? (counts.outgoing ?? 0) : 0;

    if (incoming === 0 && outgoing === 0) {
        return null;
    }

    const buildPrefilledLink = (path, criteria, params) => {
        if (powerSearch && Array.isArray(criteria) && criteria.length > 0) {
            return `${path}?criteria=${encodeCriteria(criteria)}`;
        }

        const query = toQueryString(params);

        return query ? `${path}?${query}` : path;
    };

    const incomingCriteria = [
        {field: 'transactionStatuses', operator: 'in', value: ['New', 'Matched']},
    ];
    const incomingParams = {transactionStatuses: ['New', 'Matched']};
    const incomingTo = buildPrefilledLink('/manage/payments', incomingCriteria, incomingParams);

    const outgoingCriteria = [
        {field: 'transactionStatuses', operator: 'in', value: ['New']},
        {field: 'hasExpense', operator: 'equals', value: false},
    ];
    const outgoingParams = {transactionStatuses: ['New'], hasExpense: false};
    const outgoingTo = buildPrefilledLink('/manage/expenses', outgoingCriteria, outgoingParams);

    return (
        <Tile title="Bank transactions" icon="BuildingColumns">
            <div className="flex flex-col sm:flex-row gap-2">
                {incoming > 0 && (
                    <ToHandleRow
                        to={incomingTo}
                        icon="MoneyBillWave"
                        count={`${incoming} Payments`}
                        label="Incoming to match"
                    />
                )}
                {outgoing > 0 && (
                    <ToHandleRow
                        to={outgoingTo}
                        icon="Receipt"
                        count={`${outgoing} Expenses`}
                        label="Outgoing to link"
                    />
                )}
            </div>
        </Tile>
    );
};

const ToHandleRow = ({ to, icon, count, label }) => {
    return (
        <Link
            to={to}
            className="flex-1 flex flex-row items-center gap-3 p-3 rounded-lg surface-1 surface-outline hover:opacity-90 transition-opacity"
        >
            <div className="shrink-0 w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center text-brand fill-brand">
                <Icon icon={icon} size="1x" />
            </div>
            <div className="grow min-w-0">
                <p className="font-bold text-lg leading-tight">{count}</p>
                <p className="text-sm text-muted truncate">{label}</p>
            </div>
            <Icon icon="RightChevron" size="1x" className="shrink-0 text-muted" />
        </Link>
    );
};

export default BankTransactionsToHandle;
